import { BaseEdge, type EdgeProps } from '@xyflow/react';

export interface TrunkEdgeData extends Record<string, unknown> {
  /** Where the vertical run sits, in flow coordinates. */
  trunkX: number;
}

/**
 * How far below the source the connector turns. The source handle already sits
 * clear of the expand pill, so this only has to look deliberate.
 */
const DROP = 16;
const RADIUS = 10;

/**
 * The connector between a manager and a report, for both shapes the layout
 * produces: down, across to a vertical run, down it, and in.
 *
 * Every report of one manager turns at the same height and shares the same
 * vertical run, which is what makes a list of twelve read as one bracket.
 * React Flow's own smoothstep cannot do it — it routes each edge independently,
 * turning at the midpoint between its own two ends, so a stacked report and an
 * expanded one bent at different heights and the result looked broken.
 */
export function TrunkEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  style,
  data,
}: EdgeProps & { data?: TrunkEdgeData }) {
  const trunkX = data?.trunkX ?? targetX;
  const turnY = sourceY + DROP;

  return (
    <BaseEdge
      path={orthogonalPath([
        { x: sourceX, y: sourceY },
        { x: sourceX, y: turnY },
        { x: trunkX, y: turnY },
        { x: trunkX, y: targetY },
        { x: targetX, y: targetY },
      ])}
      markerEnd={markerEnd}
      style={style}
    />
  );
}

interface Point {
  x: number;
  y: number;
}

/**
 * A path through the points with rounded corners, skipping any that repeat.
 *
 * Dropping the repeats is what makes one builder serve both shapes: when a
 * report sits directly under its manager the first two corners collapse and
 * this is a straight line, with no degenerate arcs left behind.
 */
function orthogonalPath(points: Point[]): string {
  const via = points.filter(
    (point, index) =>
      index === 0 ||
      Math.hypot(point.x - (points[index - 1] as Point).x, point.y - (points[index - 1] as Point).y) > 0.5,
  );
  if (via.length < 2) return `M ${points[0]?.x ?? 0},${points[0]?.y ?? 0}`;

  const parts = [`M ${via[0]?.x},${via[0]?.y}`];
  for (let index = 1; index < via.length - 1; index += 1) {
    const from = via[index - 1] as Point;
    const corner = via[index] as Point;
    const to = via[index + 1] as Point;

    const radius = Math.min(RADIUS, distance(from, corner) / 2, distance(corner, to) / 2);
    parts.push(`L ${towards(corner, from, radius).x},${towards(corner, from, radius).y}`);
    parts.push(`Q ${corner.x},${corner.y} ${towards(corner, to, radius).x},${towards(corner, to, radius).y}`);
  }
  parts.push(`L ${via.at(-1)?.x},${via.at(-1)?.y}`);
  return parts.join(' ');
}

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** `by` pixels from `point` in the direction of `towardsPoint`. */
function towards(point: Point, towardsPoint: Point, by: number): Point {
  const length = distance(point, towardsPoint) || 1;
  return {
    x: point.x + ((towardsPoint.x - point.x) / length) * by,
    y: point.y + ((towardsPoint.y - point.y) / length) * by,
  };
}
